/**
 * A singleton class that manages intervals throughout the application.
 * This prevents intervals from running indefinitely if Redux state is reset,
 * as interval IDs are stored in memory rather than in Redux.
 */
class IntervalManager {
  private static instance: IntervalManager;

  private intervals: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Gets the singleton instance of IntervalManager
   */
  static getInstance(): IntervalManager {
    if (!IntervalManager.instance) IntervalManager.instance = new IntervalManager();

    return IntervalManager.instance;
  }

  /**
   * Creates a new interval with the given key.
   * If an interval with this key already exists, it is cleared first.
   *
   * @param key - Unique identifier for the interval
   * @param callback - Function to execute at each interval
   * @param delay - Interval delay in milliseconds
   * @returns The created interval ID
   */
  create(key: string, callback: () => void, delay: number): NodeJS.Timeout {
    if (this.intervals.has(key)) this.clear(key);

    const intervalId = setInterval(callback, delay);
    this.intervals.set(key, intervalId);
    return intervalId;
  }

  /**
   * Gets an interval by its key
   *
   * @param key - Unique identifier for the interval
   * @returns The interval ID or undefined if not found
   */
  get(key: string): NodeJS.Timeout | undefined {
    return this.intervals.get(key);
  }

  /**
   * Checks if an interval with the given key exists
   *
   * @param key - Unique identifier for the interval
   * @returns True if the interval exists, false otherwise
   */
  has(key: string): boolean {
    return this.intervals.has(key);
  }

  /**
   * Clears an interval by its key
   *
   * @param key - Unique identifier for the interval
   */
  clear(key: string): void {
    const intervalId = this.intervals.get(key);
    if (intervalId) {
      clearInterval(intervalId);
      this.intervals.delete(key);
    }
  }

  /**
   * Clears all intervals managed by this instance
   */
  clearAll(): void {
    this.intervals.forEach((intervalId) => clearInterval(intervalId));
    this.intervals.clear();
  }
}

export default IntervalManager;
